/**
 * Authorization snapshot + active-clinic selection for a signed-in user.
 *
 * /v1/me/authz is render-only input: it lists every live membership and the
 * effective permission set for the selected clinic; every action is still
 * re-enforced server-side. Ambiguity (several memberships, nothing stored)
 * is answered by the person via the chooser — never guessed.
 *
 * ZOY-610 — the selected clinic is put on the wire SYNCHRONOUSLY: the stored
 * id before the snapshot is asked for, the server's answer before any child
 * renders, so no clinic request of a multi-membership user ever leaves
 * without its x-clinic-id. A 403 CLINIC_CONTEXT_INVALID / CLINIC_INACTIVE
 * from ANY request re-resolves the snapshot (chooser / inactive / no-access,
 * never a stale screen); a switch in another tab freezes this one until it
 * is reloaded; a switch here purges every clinic-scoped cache entry.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/errors';
import { clinicIsActive, fetchMeAuthz, type MeAuthzResponse } from './api';
import {
  clearStoredClinic,
  freezeClinicContext,
  frozenClinicContext,
  onClinicContextFrozen,
  onClinicContextLost,
  readStoredClinic,
  setSelectedClinicId,
  storeClinic,
  subscribeStoredClinic,
  type ClinicContextLostCode,
  type FrozenContext,
} from './clinicContext';
import { can, type AuthzView, type PermissionKey } from './permissions';
import { qk } from '../api/keys';

export type AuthzState =
  | { status: 'loading' }
  | { status: 'error'; error: unknown }
  | { status: 'chooseClinic'; snapshot: MeAuthzResponse }
  /** ZOY-612: the selected clinic is temporarily deactivated by the platform. */
  | { status: 'inactive'; snapshot: MeAuthzResponse }
  | { status: 'suspended'; snapshot: MeAuthzResponse }
  | { status: 'ready'; snapshot: MeAuthzResponse; view: AuthzView };

interface AuthzContextValue {
  state: AuthzState;
  /** Selected clinic id (also travels as x-clinic-id on clinic calls). */
  clinicId: string | null;
  selectClinic: (clinicId: string) => void;
  clearSelection: () => void;
  can: (key: PermissionKey) => boolean;
  refetch: () => void;
  /** Another tab switched the clinic: this tab is frozen until reloaded (or signed out). */
  frozen: FrozenContext | null;
  /** The server refused the clinic this tab was working in, mid-session. */
  lost: ClinicContextLostCode | null;
}

const AuthzContext = createContext<AuthzContextValue | null>(null);

/**
 * ZOY-407: short staleness so the deliberate focus-refetch actually fires.
 * Bound: a re-focused tab re-reads the snapshot when it is older than this,
 * so a revoked permission's surface disappears within ~15s of client
 * staleness plus the backend's own propagation (~45s, ZOY-376) — with no
 * manual reload. Ordinary data queries keep the app-wide no-focus-refetch
 * default.
 */
export const AUTHZ_SNAPSHOT_STALE_MS = 15_000;

/** Query families that are NOT clinic-scoped and survive a clinic switch. */
const KEEP_ON_SWITCH = new Set(['authz', 'me', 'profile']);

/** Everything read for the previous clinic goes; nothing of it can render in the next. */
export function purgeClinicScopedQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (q) => !KEEP_ON_SWITCH.has(String(q.queryKey[0])),
  });
}

export function AuthzProvider({ uid, children }: { uid: string; children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [storedEpoch, bumpStoredEpoch] = useReducer((n: number) => n + 1, 0);
  const [frozen, setFrozen] = useState<FrozenContext | null>(() => frozenClinicContext());
  const [lost, setLost] = useState<ClinicContextLostCode | null>(null);

  // Resolve the candidate clinic before asking for the snapshot: stored
  // selection if any; the backend auto-selects when exactly one membership.
  // storedEpoch re-reads it after a self-heal drop below. The module-level
  // header source is seeded HERE, synchronously, so nothing rendered below
  // can issue a clinic request before the intended x-clinic-id is known.
  const stored = useMemo(
    () => {
      const s = readStoredClinic(uid);
      setSelectedClinicId(s);
      return s;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uid, storedEpoch],
  );

  const query = useQuery({
    queryKey: qk.authz(uid, stored),
    queryFn: () => fetchMeAuthz(stored ?? undefined),
    staleTime: AUTHZ_SNAPSHOT_STALE_MS,
    refetchOnWindowFocus: true,
    retry: 1,
  });
  const snapshot = query.data;

  // The server's answer is the selected clinic — applied during render, i.e.
  // before any child renders and can fire a request (an effect would run one
  // commit too late). Idempotent module write, so re-renders are harmless.
  setSelectedClinicId(snapshot ? snapshot.clinicId : stored);

  const heal = useCallback(() => {
    clearStoredClinic(uid);
    setSelectedClinicId(null);
    purgeClinicScopedQueries(queryClient);
    bumpStoredEpoch();
  }, [uid, queryClient]);

  // ZOY-306: a stored clinic the server refuses outright (moved membership,
  // stale key) would fail Retry forever. Drop it and re-resolve — the backend
  // auto-selects a single membership, or the chooser renders. Fail-closed
  // the whole way; nothing is granted.
  useEffect(() => {
    if (!query.isError || stored === null) return;
    const err = query.error;
    if (err instanceof ApiError && err.code === 'CLINIC_CONTEXT_INVALID') heal();
  }, [query.isError, query.error, stored, heal]);

  // A stored selection the server no longer honors is dropped.
  useEffect(() => {
    if (stored !== null && snapshot && snapshot.clinicId === null) clearStoredClinic(uid);
  }, [snapshot, stored, uid]);

  // ZOY-610: mid-session loss on ANY clinic request. No membership any more →
  // forget the selection and re-resolve (chooser / no-access). Clinic
  // deactivated → the snapshot itself will now say so (inactive state).
  useEffect(
    () =>
      onClinicContextLost((code) => {
        setLost(code);
        if (code === 'CLINIC_CONTEXT_INVALID') heal();
        else void queryClient.invalidateQueries({ queryKey: ['authz'] });
      }),
    [heal, queryClient],
  );

  // ZOY-610 cross-tab: another tab changed this uid's stored clinic. This tab
  // never follows silently — it freezes (the HTTP layer refuses clinic calls)
  // and asks the person to reload or sign out.
  useEffect(() => onClinicContextFrozen(setFrozen), []);
  useEffect(
    () =>
      subscribeStoredClinic(uid, (next) => {
        if (next === stored) return; // our own value echoed back — nothing changed
        freezeClinicContext({ clinicId: next });
      }),
    [uid, stored],
  );

  const selectClinic = useCallback(
    (clinicId: string) => {
      storeClinic(uid, clinicId);
      setSelectedClinicId(clinicId);
      setLost(null);
      purgeClinicScopedQueries(queryClient);
      // The stored id is part of the snapshot's key: re-read it so the next
      // fetch is a NEW query that carries the header (an invalidation alone
      // would refetch the old, header-less key and land on the chooser again).
      bumpStoredEpoch();
    },
    [uid, queryClient],
  );

  const clearSelection = useCallback(() => {
    clearStoredClinic(uid);
    setSelectedClinicId(null);
    setLost(null);
    purgeClinicScopedQueries(queryClient);
    bumpStoredEpoch();
  }, [uid, queryClient]);

  const state: AuthzState = useMemo(() => {
    if (query.isPending) return { status: 'loading' };
    if (query.isError) return { status: 'error', error: query.error };
    const snap = query.data;
    if (snap.clinicId === null) return { status: 'chooseClinic', snapshot: snap };
    if (!clinicIsActive(snap)) return { status: 'inactive', snapshot: snap };
    if (snap.membershipStatus !== null && snap.membershipStatus !== 'active') {
      return { status: 'suspended', snapshot: snap };
    }
    return {
      status: 'ready',
      snapshot: snap,
      view: { fullClinicAdmin: snap.fullClinicAdmin, permissions: new Set(snap.permissions) },
    };
  }, [query.isPending, query.isError, query.error, query.data]);

  // Reactive clinic id: the snapshot (query.data) is the authoritative selected
  // clinic — the backend auto-selects it when exactly one membership exists.
  const value = useMemo<AuthzContextValue>(
    () => ({
      state,
      clinicId: snapshot?.clinicId ?? null,
      selectClinic,
      clearSelection,
      can: (key) => (state.status === 'ready' ? can(state.view, key) : false),
      refetch: () => void query.refetch(),
      frozen,
      lost,
    }),
    [state, snapshot, selectClinic, clearSelection, query, frozen, lost],
  );

  return <AuthzContext.Provider value={value}>{children}</AuthzContext.Provider>;
}

export function useAuthz(): AuthzContextValue {
  const ctx = useContext(AuthzContext);
  if (!ctx) throw new Error('useAuthz outside AuthzProvider');
  return ctx;
}
